export type BgmCueId =
  | "menu"
  | "chapter_01"
  | "chapter_02"
  | "chapter_03"
  | "chapter_04"
  | "chapter_05"
  | "chapter_06"
  | "theater"
  | "comedy"
  | "battle"
  | "dream_warm"
  | "dream_uneasy"
  | "climax"
  | "result"
  | "nightmare"
  | "ending";

export type SeCueId =
  | "ui_select"
  | "ui_confirm"
  | "ui_cancel"
  | "ui_error"
  | "dialogue_advance"
  | "curtain_open"
  | "curtain_close"
  | "window_touch"
  | "scene_transition"
  | "night_gaze"
  | "morning"
  | "card_select"
  | "card_reveal"
  | "object_appear"
  | "object_move"
  | "depth_shift"
  | "object_merge"
  | "object_exit"
  | "focus_change"
  | "energy_spend"
  | "energy_recover"
  | "devotion"
  | "happiness_up"
  | "happiness_down"
  | "time_chime"
  | "result_ideal"
  | "result_oversleep"
  | "result_nightmare"
  | "applause";

export interface AudioCueDefinition {
  url: string;
  gain: number;
  loop?: boolean;
  provided: boolean;
}

export interface AudioCatalogEntry<TCueId extends string> {
  id: TCueId;
  label: string;
  category: string;
  usage: string;
  suggestedLength: string;
}

const suppliedBgmUrls = import.meta.glob(
  "/assets-source/audio/bgm/*.{mp3,ogg,wav}",
  { eager: true, query: "?url", import: "default" },
) as Record<string, string>;
const suppliedSeUrls = import.meta.glob(
  "/assets-source/audio/se/*.{mp3,ogg,wav}",
  { eager: true, query: "?url", import: "default" },
) as Record<string, string>;

const placeholderBgm = {
  menu: new URL("../assets/audio/placeholders/bgm-menu.wav", import.meta.url).href,
  battle: new URL("../assets/audio/placeholders/bgm-battle.wav", import.meta.url).href,
  result: new URL("../assets/audio/placeholders/bgm-result.wav", import.meta.url).href,
} as const;

const placeholderSe = {
  ui_select: new URL("../assets/audio/placeholders/se-ui-select.wav", import.meta.url).href,
  ui_confirm: new URL("../assets/audio/placeholders/se-ui-confirm.wav", import.meta.url).href,
  card_reveal: new URL("../assets/audio/placeholders/se-card-reveal.wav", import.meta.url).href,
  object_appear: new URL("../assets/audio/placeholders/se-object-appear.wav", import.meta.url).href,
  object_move: new URL("../assets/audio/placeholders/se-object-move.wav", import.meta.url).href,
  object_merge: new URL("../assets/audio/placeholders/se-object-merge.wav", import.meta.url).href,
  object_exit: new URL("../assets/audio/placeholders/se-object-exit.wav", import.meta.url).href,
  happiness_up: new URL("../assets/audio/placeholders/se-happiness-up.wav", import.meta.url).href,
  happiness_down: new URL("../assets/audio/placeholders/se-happiness-down.wav", import.meta.url).href,
  time_chime: new URL("../assets/audio/placeholders/se-time-chime.wav", import.meta.url).href,
} as const;

function suppliedUrl(
  supplied: Readonly<Record<string, string>>,
  cueId: string,
): string | null {
  for (const [path, url] of Object.entries(supplied)) {
    const fileName = path.split(/[\\/]/).at(-1) ?? "";
    const stem = fileName.replace(/\.[^.]+$/, "");
    if (stem === cueId || stem.startsWith(cueId + "--")) return url;
  }
  return null;
}

function bgmCue(
  id: BgmCueId,
  fallback: string,
  gain: number,
  loop = true,
): AudioCueDefinition {
  const supplied = suppliedUrl(suppliedBgmUrls, id);
  return { url: supplied ?? fallback, gain, loop, provided: Boolean(supplied) };
}

function seCue(
  id: SeCueId,
  fallback: string,
  gain: number,
): AudioCueDefinition {
  const supplied = suppliedUrl(suppliedSeUrls, id);
  return { url: supplied ?? fallback, gain, provided: Boolean(supplied) };
}

export const BGM_CATALOG: readonly AudioCatalogEntry<BgmCueId>[] = Object.freeze([
  { id: "menu", label: "タイトル・メニュー", category: "案内", usage: "タイトル、メインメニュー、設定画面", suggestedLength: "90〜180秒・ループ" },
  { id: "chapter_01", label: "第一章　犬の夢", category: "物語", usage: "第一章「帰る匂い」／自由対戦「帰らずの散歩道」", suggestedLength: "章内ループ" },
  { id: "chapter_02", label: "第二章　遠足の夢", category: "物語", usage: "第二章「竜の待つ山」／自由対戦「竜の待つ山」", suggestedLength: "章内ループ" },
  { id: "chapter_03", label: "第三章　サンタの夢", category: "物語", usage: "第三章「鈴の鳴らない夜」／自由対戦「暖炉の前」", suggestedLength: "章内ループ" },
  { id: "chapter_04", label: "第四章　鏡の夢", category: "物語", usage: "第四章「弾けなかった音」／自由対戦「冬の広場」", suggestedLength: "章内ループ" },
  { id: "chapter_05", label: "第五章　ロボットの夢", category: "物語", usage: "第五章「長い休暇」／自由対戦「雨音の書斎」", suggestedLength: "章内ループ" },
  { id: "chapter_06", label: "第六章　ピアノの夢", category: "物語", usage: "第六章「待ってる」／自由対戦「重なる舞台」", suggestedLength: "章内ループ" },
  { id: "theater", label: "劇場の平常", category: "劇場", usage: "幕間、支配人との会話、落ち着いた舞台裏", suggestedLength: "90〜180秒・ループ" },
  { id: "comedy", label: "劇場の軽口", category: "劇場", usage: "先輩たちとの軽いやり取り、職場らしい場面", suggestedLength: "60〜120秒・ループ" },
  { id: "battle", label: "通常の演目", category: "演目", usage: "曲が未指定の演目で使う予備BGM", suggestedLength: "90〜180秒・ループ" },
  { id: "dream_warm", label: "温かな記憶", category: "演目", usage: "夢の読み替え、和解、懐かしい記憶", suggestedLength: "60〜150秒・ループ" },
  { id: "dream_uneasy", label: "不穏な夢", category: "演目", usage: "悪夢へ傾く場面、夜の目、焦燥", suggestedLength: "60〜120秒・ループ" },
  { id: "climax", label: "演目の山場", category: "演目", usage: "章の決着、第六章の演奏など大きな転換", suggestedLength: "90〜180秒・ループ" },
  { id: "result", label: "よい朝", category: "結果", usage: "理想付近で終えた結果と穏やかな後日談", suggestedLength: "30〜90秒・ループ" },
  { id: "nightmare", label: "届かなかった朝", category: "結果", usage: "悪夢、寝坊、敗北寄りの結果", suggestedLength: "30〜90秒・ループ" },
  { id: "ending", label: "エンドロール", category: "結末", usage: "最終章後のスタッフロール", suggestedLength: "180〜300秒・非ループ" },
]);

export const SE_CATALOG: readonly AudioCatalogEntry<SeCueId>[] = Object.freeze([
  { id: "ui_select", label: "選択", category: "UI", usage: "項目移動、軽い選択", suggestedLength: "0.05〜0.15秒" },
  { id: "ui_confirm", label: "決定", category: "UI", usage: "決定、画面開始", suggestedLength: "0.1〜0.3秒" },
  { id: "ui_cancel", label: "戻る", category: "UI", usage: "取消、ひとつ前へ戻る", suggestedLength: "0.1〜0.3秒" },
  { id: "ui_error", label: "選択不可", category: "UI", usage: "不正対象、コスト不足", suggestedLength: "0.15〜0.35秒" },
  { id: "dialogue_advance", label: "台詞送り", category: "物語", usage: "会話を一文進める", suggestedLength: "0.03〜0.12秒" },
  { id: "curtain_open", label: "開幕", category: "物語", usage: "幕が開き、演目へ入る", suggestedLength: "0.8〜2.5秒" },
  { id: "curtain_close", label: "幕引き", category: "物語", usage: "演目終了、暗転", suggestedLength: "0.8〜2.5秒" },
  { id: "window_touch", label: "《窓》に触れる", category: "物語", usage: "水面へ手を入れ、夢へ干渉する", suggestedLength: "0.5〜1.5秒" },
  { id: "scene_transition", label: "場面転換", category: "物語", usage: "夢景色や時代が切り替わる", suggestedLength: "0.4〜1.2秒" },
  { id: "night_gaze", label: "夜の目", category: "物語", usage: "観客の視線が強まる短い不穏音", suggestedLength: "0.6〜1.8秒" },
  { id: "morning", label: "朝への転換", category: "物語", usage: "夢から翌朝の後日談へ", suggestedLength: "0.8〜2.0秒" },
  { id: "card_select", label: "カードを取る", category: "試合", usage: "公開札・手札の選択", suggestedLength: "0.1〜0.3秒" },
  { id: "card_reveal", label: "カード公開", category: "試合", usage: "予約カードを表にする", suggestedLength: "0.2〜0.5秒" },
  { id: "object_appear", label: "オブジェクト出現", category: "試合", usage: "夢オブジェクトを盤面へ置く", suggestedLength: "0.3〜0.7秒" },
  { id: "object_move", label: "レーン移動", category: "試合", usage: "左右・フィールド間の移動", suggestedLength: "0.2〜0.6秒" },
  { id: "depth_shift", label: "深度移動", category: "試合", usage: "時間経過で一段浅くなる", suggestedLength: "0.3〜0.8秒" },
  { id: "object_merge", label: "合体", category: "試合", usage: "二つのオブジェクトが一つになる", suggestedLength: "0.4〜0.9秒" },
  { id: "object_exit", label: "退場", category: "試合", usage: "端や深度0から夢を去る", suggestedLength: "0.3〜0.8秒" },
  { id: "focus_change", label: "注目変更", category: "試合", usage: "ご主人の注目レーンを変える", suggestedLength: "0.15〜0.4秒" },
  { id: "energy_spend", label: "コスト消費", category: "試合", usage: "演出力を支払う", suggestedLength: "0.15〜0.4秒" },
  { id: "energy_recover", label: "コスト回復", category: "試合", usage: "時間開始時に演出力が戻る", suggestedLength: "0.2〜0.6秒" },
  { id: "devotion", label: "献身", category: "試合", usage: "しもべ固有の献身を使う", suggestedLength: "0.6〜1.5秒" },
  { id: "happiness_up", label: "幸福度上昇", category: "試合", usage: "幸福度メーターが上がる", suggestedLength: "0.3〜0.7秒" },
  { id: "happiness_down", label: "幸福度低下", category: "試合", usage: "幸福度メーターが下がる", suggestedLength: "0.3〜0.7秒" },
  { id: "time_chime", label: "時間進行", category: "試合", usage: "一時間の終わりと次時間の開始", suggestedLength: "0.6〜1.2秒" },
  { id: "result_ideal", label: "理想着地", category: "結果", usage: "理想幸福度付近で朝を迎える", suggestedLength: "0.8〜2.0秒" },
  { id: "result_oversleep", label: "寝坊", category: "結果", usage: "幸福度が高すぎる結果", suggestedLength: "0.8〜2.0秒" },
  { id: "result_nightmare", label: "悪夢", category: "結果", usage: "幸福度が0以下になる", suggestedLength: "0.8〜2.0秒" },
  { id: "applause", label: "喝采", category: "結果", usage: "劇場で演目を締める", suggestedLength: "1.5〜4.0秒" },
]);

export const BGM_CUES: Readonly<Record<BgmCueId, AudioCueDefinition>> = {
  menu: bgmCue("menu", placeholderBgm.menu, 0.28),
  chapter_01: bgmCue("chapter_01", placeholderBgm.battle, 0.34),
  chapter_02: bgmCue("chapter_02", placeholderBgm.battle, 0.34),
  chapter_03: bgmCue("chapter_03", placeholderBgm.battle, 0.34),
  chapter_04: bgmCue("chapter_04", placeholderBgm.battle, 0.32),
  chapter_05: bgmCue("chapter_05", placeholderBgm.battle, 0.34),
  chapter_06: bgmCue("chapter_06", placeholderBgm.battle, 0.35),
  theater: bgmCue("theater", placeholderBgm.menu, 0.34),
  comedy: bgmCue("comedy", placeholderBgm.menu, 0.35),
  battle: bgmCue("battle", placeholderBgm.battle, 0.36),
  dream_warm: bgmCue("dream_warm", placeholderBgm.result, 0.38),
  dream_uneasy: bgmCue("dream_uneasy", placeholderBgm.battle, 0.34),
  climax: bgmCue("climax", placeholderBgm.battle, 0.4),
  result: bgmCue("result", placeholderBgm.result, 0.42),
  nightmare: bgmCue("nightmare", placeholderBgm.battle, 0.38),
  ending: bgmCue("ending", placeholderBgm.result, 0.42, false),
};

export const SE_CUES: Readonly<Record<SeCueId, AudioCueDefinition>> = {
  ui_select: seCue("ui_select", placeholderSe.ui_select, 0.42),
  ui_confirm: seCue("ui_confirm", placeholderSe.ui_confirm, 0.46),
  ui_cancel: seCue("ui_cancel", placeholderSe.ui_select, 0.42),
  ui_error: seCue("ui_error", placeholderSe.happiness_down, 0.48),
  dialogue_advance: seCue("dialogue_advance", placeholderSe.ui_select, 0.34),
  curtain_open: seCue("curtain_open", placeholderSe.object_appear, 0.52),
  curtain_close: seCue("curtain_close", placeholderSe.object_exit, 0.5),
  window_touch: seCue("window_touch", placeholderSe.object_merge, 0.5),
  scene_transition: seCue("scene_transition", placeholderSe.object_move, 0.46),
  night_gaze: seCue("night_gaze", placeholderSe.happiness_down, 0.5),
  morning: seCue("morning", placeholderSe.time_chime, 0.52),
  card_select: seCue("card_select", placeholderSe.ui_select, 0.4),
  card_reveal: seCue("card_reveal", placeholderSe.card_reveal, 0.52),
  object_appear: seCue("object_appear", placeholderSe.object_appear, 0.58),
  object_move: seCue("object_move", placeholderSe.object_move, 0.5),
  depth_shift: seCue("depth_shift", placeholderSe.object_move, 0.48),
  object_merge: seCue("object_merge", placeholderSe.object_merge, 0.62),
  object_exit: seCue("object_exit", placeholderSe.object_exit, 0.5),
  focus_change: seCue("focus_change", placeholderSe.ui_select, 0.4),
  energy_spend: seCue("energy_spend", placeholderSe.card_reveal, 0.42),
  energy_recover: seCue("energy_recover", placeholderSe.happiness_up, 0.46),
  devotion: seCue("devotion", placeholderSe.object_merge, 0.62),
  happiness_up: seCue("happiness_up", placeholderSe.happiness_up, 0.56),
  happiness_down: seCue("happiness_down", placeholderSe.happiness_down, 0.56),
  time_chime: seCue("time_chime", placeholderSe.time_chime, 0.58),
  result_ideal: seCue("result_ideal", placeholderSe.time_chime, 0.62),
  result_oversleep: seCue("result_oversleep", placeholderSe.happiness_up, 0.58),
  result_nightmare: seCue("result_nightmare", placeholderSe.happiness_down, 0.62),
  applause: seCue("applause", placeholderSe.object_merge, 0.56),
};
